// ---------------------------------------------------------------------------
// Survive on BCH — Hustle Mode run generator (SIMULATED)
//
// Builds a fresh, randomized day at the shop from a per-session seed:
//  - a varied, balanced queue of customers sampled from an archetype pool
//  - per-sale prices jittered within each archetype's band
//  - a shuffled set of network-condition profiles (a couple of BTC fee spikes
//    land at unpredictable rounds each run)
//
// The teaching shape is preserved on purpose — every run still guarantees a
// spread of BCH lovers, steer-to-BCH opportunities, a hardline "won't budge"
// customer or two, and at least one nasty congestion spike — so BCH's story
// still lands. Only the specifics vary.
//
// Everything is deterministic given the seed, matching the pure architecture.
// ---------------------------------------------------------------------------

import type { Customer, Flexibility, RoundConditions } from "./types";

/** How many customers a single run serves. */
export const RUN_LENGTH = 12;

// ---- Seeded RNG (mulberry32) ----------------------------------------------

export type Rng = () => number;

/** Deterministic PRNG in [0,1). */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A fresh, hard-to-guess seed for a new run. */
export function randomSeed(): number {
  return (Math.floor(Math.random() * 0xffffffff) ^ Date.now()) >>> 0;
}

function pick<T>(rng: Rng, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/** Fisher–Yates shuffle producing a NEW array. */
function shuffle<T>(rng: Rng, arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Random integer in [min, max]. */
function randInt(rng: Rng, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

// ---- Customer archetypes ---------------------------------------------------

interface Archetype {
  key: string;
  icon: string;
  name: string;
  /** Item options — one is chosen at random. */
  items: string[];
  prefers: Customer["prefers"];
  flexibility: Flexibility;
  lightningCapable: boolean;
  cardRisky?: boolean;
  insistsOn?: Customer["insistsOn"];
  /** Price band [min, max] for this archetype's sale. */
  price: [number, number];
  /** Flavor text (may reference the archetype's disposition). */
  flavor: string;
  /**
   * Bucket used to guarantee a balanced mix:
   *  - "bch": already a BCH fan (easy win, shows BCH working)
   *  - "steer": prefers card/BTC but persuadable (the teaching moment)
   *  - "hardline": binding maxi / card-only (the tough dilemma)
   */
  bucket: "bch" | "steer" | "hardline";
}

const ARCHETYPES: Archetype[] = [
  // ---- BCH fans -----------------------------------------------------------
  {
    key: "regular",
    icon: "☕",
    name: "Morning Regular",
    items: ["coffee & bagel", "flat white", "espresso & croissant"],
    prefers: "bch",
    flexibility: "flexible",
    lightningCapable: true,
    price: [5, 9],
    flavor: "A loyal regular who loves paying in BCH — a tiny sale where fees matter most.",
    bucket: "bch",
  },
  {
    key: "student",
    icon: "🌮",
    name: "Hungry Student",
    items: ["taco platter", "burrito", "noodle bowl"],
    prefers: "bch",
    flexibility: "flexible",
    lightningCapable: true,
    price: [8, 14],
    flavor: "Small order, tight budget. Pays in BCH like the locals do.",
    bucket: "bch",
  },
  {
    key: "artist",
    icon: "🎨",
    name: "Local Artist",
    items: ["art supplies", "canvas set", "sketchbooks"],
    prefers: "bch",
    flexibility: "flexible",
    lightningCapable: true,
    price: [20, 40],
    flavor: "Part of the BCH scene. Scans the QR before you even ask.",
    bucket: "bch",
  },
  {
    key: "neighbor",
    icon: "🪴",
    name: "Neighbor",
    items: ["potted plant", "bag of soil", "herb garden kit"],
    prefers: "bch",
    flexibility: "flexible",
    lightningCapable: false,
    price: [15, 30],
    flavor: "From down the street. Been meaning to try paying in BCH.",
    bucket: "bch",
  },
  // ---- Steer-to-BCH opportunities ----------------------------------------
  {
    key: "tourist",
    icon: "🧢",
    name: "Tourist",
    items: ["souvenir cap", "postcard set", "fridge magnets"],
    prefers: "card",
    flexibility: "flexible",
    lightningCapable: false,
    price: [12, 28],
    flavor: "Reaches for a card out of habit — but they're curious and easygoing. Suggest BCH and they'll happily give it a try.",
    bucket: "steer",
  },
  {
    key: "office",
    icon: "🥪",
    name: "Office Worker",
    items: ["lunch combo", "salad & juice", "sandwich platter"],
    prefers: "btc-lightning",
    flexibility: "flexible",
    lightningCapable: true,
    price: [10, 20],
    flavor: "Crypto-savvy and in a hurry — happy to zap it over Lightning, or try BCH.",
    bucket: "steer",
  },
  {
    key: "bookworm",
    icon: "📚",
    name: "Bookworm",
    items: ["stack of books", "boxed set", "rare paperback"],
    prefers: "btc-lightning",
    flexibility: "flexible",
    lightningCapable: true,
    price: [25, 45],
    flavor: "Happy to pay in crypto — open to whatever's easiest.",
    bucket: "steer",
  },
  {
    key: "bigspender",
    icon: "💳",
    name: "Big Spender",
    items: ["gift hamper", "designer bag", "electronics bundle"],
    prefers: "card",
    flexibility: "firm",
    lightningCapable: false,
    cardRisky: true,
    price: [110, 180],
    flavor: "Set on a card for this big order — and something about it feels risky. They'll grumble, but you could talk them into a final, chargeback-proof payment.",
    bucket: "steer",
  },
  {
    key: "closer",
    icon: "🍷",
    name: "Evening Customer",
    items: ["bottle of wine", "cheese board", "gift box"],
    prefers: "btc-lightning",
    flexibility: "firm",
    lightningCapable: true,
    price: [30, 55],
    flavor: "A Lightning fan set on zapping it over. Steer them elsewhere and they'll grudgingly go along.",
    bucket: "steer",
  },
  {
    key: "cardhabit",
    icon: "🛍️",
    name: "Card-Tapper",
    items: ["new sneakers", "jacket", "backpack"],
    prefers: "card",
    flexibility: "flexible",
    lightningCapable: false,
    price: [40, 75],
    flavor: "Taps a card everywhere out of pure habit — but has no strong feelings. A gentle nudge toward BCH would stick.",
    bucket: "steer",
  },
  // ---- Hardline "won't budge" customers -----------------------------------
  {
    key: "maxi",
    icon: "🟠",
    name: "Bitcoin Maxi",
    items: ["wireless earbuds", "hardware wallet", "graphic tee"],
    prefers: "btc-onchain",
    flexibility: "binding",
    lightningCapable: false,
    price: [50, 90],
    flavor: "\"BCH isn't Bitcoin.\" He'll pay on-chain BTC or walk — hope the network isn't congested.",
    bucket: "hardline",
  },
  {
    key: "dev",
    icon: "💻",
    name: "Remote Dev",
    items: ["refurb keyboard", "mechanical mouse", "USB hub"],
    prefers: "btc-onchain",
    flexibility: "binding",
    lightningCapable: true,
    insistsOn: ["btc-onchain", "btc-lightning"],
    price: [60, 100],
    flavor: "A BTC-only dev — no BCH, but at least runs Lightning. Pick your rail carefully if fees are up.",
    bucket: "hardline",
  },
  {
    key: "birthday",
    icon: "🍰",
    name: "Birthday Shopper",
    items: ["custom cake", "party platter", "cupcake tower"],
    prefers: "card",
    flexibility: "binding",
    lightningCapable: false,
    price: [35, 60],
    flavor: "\"Crypto? I wouldn't even know where to start.\" Card only — take the fee and chargeback risk, or lose the sale.",
    bucket: "hardline",
  },
  {
    key: "boomer",
    icon: "🧾",
    name: "Old-School Shopper",
    items: ["hardware & tools", "kitchenware", "gardening set"],
    prefers: "card",
    flexibility: "binding",
    lightningCapable: false,
    cardRisky: true,
    price: [45, 85],
    flavor: "Doesn't trust \"internet money.\" It's a card or nothing — and their bank is dispute-happy.",
    bucket: "hardline",
  },
];

// ---- Network conditions ----------------------------------------------------

interface ConditionProfile {
  btcOnchainFeeUsd: number;
  btcCongested: boolean;
  calmNotes: string[];
  spikeNote?: string;
}

const CALM_PROFILES: ConditionProfile[] = [
  { btcOnchainFeeUsd: 0.5, btcCongested: false, calmNotes: ["Quiet morning. Networks calm.", "Slow start. Both networks idle.", "Peaceful open. Fees low everywhere."] },
  { btcOnchainFeeUsd: 0.8, btcCongested: false, calmNotes: ["Steady flow. BTC mempool filling a little.", "Business as usual. Fees mild."] },
  { btcOnchainFeeUsd: 1.2, btcCongested: false, calmNotes: ["Rush over. BTC fees low.", "Comfortable pace. BTC on-chain cheap."] },
  { btcOnchainFeeUsd: 2.5, btcCongested: false, calmNotes: ["BTC fees creeping up as blocks fill.", "BTC mempool building — fees ticking higher."] },
  { btcOnchainFeeUsd: 4, btcCongested: false, calmNotes: ["BTC fees elevated but manageable.", "Busy-ish. BTC on-chain a few dollars."] },
  { btcOnchainFeeUsd: 6, btcCongested: false, calmNotes: ["Backlog clearing. BTC still a little elevated.", "BTC fees cooling after a busy stretch."] },
];

const SPIKE_PROFILES: ConditionProfile[] = [
  { btcOnchainFeeUsd: 12, btcCongested: true, calmNotes: [], spikeNote: "⚠️ BTC congested — next-block fees spiked to ~$12. BCH unaffected." },
  { btcOnchainFeeUsd: 18, btcCongested: true, calmNotes: [], spikeNote: "⚠️ Mempool floods — BTC on-chain fees jump to ~$18! BCH flat." },
  { btcOnchainFeeUsd: 22, btcCongested: true, calmNotes: [], spikeNote: "⚠️ An NFT mint clogs BTC — fees spike to ~$22! BCH a fraction of a cent." },
  { btcOnchainFeeUsd: 9, btcCongested: true, calmNotes: [], spikeNote: "⚠️ Backlog draining slowly. BTC on-chain still ~$9." },
];

function buildConditions(rng: Rng, rounds: number): RoundConditions[] {
  // 2–3 congestion spikes per run, placed at random (but not on round 1, so
  // the player eases in).
  const spikeCount = randInt(rng, 2, 3);
  const candidatePositions = shuffle(rng, Array.from({ length: rounds - 1 }, (_, i) => i + 2));
  const spikeRounds = new Set(candidatePositions.slice(0, spikeCount));

  return Array.from({ length: rounds }, (_, i) => {
    const round = i + 1;
    if (spikeRounds.has(round)) {
      const p = pick(rng, SPIKE_PROFILES);
      return {
        round,
        btcOnchainFeeUsd: p.btcOnchainFeeUsd,
        btcCongested: true,
        note: p.spikeNote ?? "⚠️ BTC congested — fees spiked. BCH unaffected.",
      };
    }
    const p = pick(rng, CALM_PROFILES);
    return {
      round,
      btcOnchainFeeUsd: p.btcOnchainFeeUsd,
      btcCongested: false,
      note: pick(rng, p.calmNotes),
    };
  });
}

// ---- Balanced customer queue ----------------------------------------------

function buildQueue(rng: Rng, seed: number, rounds: number): Customer[] {
  const byBucket = {
    bch: ARCHETYPES.filter((a) => a.bucket === "bch"),
    steer: ARCHETYPES.filter((a) => a.bucket === "steer"),
    hardline: ARCHETYPES.filter((a) => a.bucket === "hardline"),
  };

  // Target mix over 12 rounds: mostly steer opportunities + BCH fans, with a
  // couple of hardline customers. Guarantees the teaching arc every run.
  const plan: Archetype["bucket"][] = [];
  const bchCount = randInt(rng, 3, 4);
  const hardlineCount = randInt(rng, 2, 3);
  const steerCount = rounds - bchCount - hardlineCount;
  for (let i = 0; i < bchCount; i++) plan.push("bch");
  for (let i = 0; i < steerCount; i++) plan.push("steer");
  for (let i = 0; i < hardlineCount; i++) plan.push("hardline");

  // Draw archetypes per bucket, allowing repeats but preferring variety by
  // cycling through a shuffled bucket list.
  const pools = {
    bch: shuffle(rng, byBucket.bch),
    steer: shuffle(rng, byBucket.steer),
    hardline: shuffle(rng, byBucket.hardline),
  };
  const cursor = { bch: 0, steer: 0, hardline: 0 };
  const drawn: Archetype[] = plan.map((bucket) => {
    const poolPool = pools[bucket];
    const a = poolPool[cursor[bucket] % poolPool.length];
    cursor[bucket] += 1;
    return a;
  });

  // Shuffle the order so buckets aren't clustered, then assign rounds + jitter.
  const ordered = shuffle(rng, drawn);
  return ordered.map((a, i) => ({
    id: `run-${seed}-${i}-${a.key}`,
    round: i + 1,
    icon: a.icon,
    name: a.name,
    item: pick(rng, a.items),
    priceUsd: randInt(rng, a.price[0], a.price[1]),
    prefers: a.prefers,
    flexibility: a.flexibility,
    lightningCapable: a.lightningCapable,
    cardRisky: a.cardRisky,
    insistsOn: a.insistsOn,
    flavor: a.flavor,
  }));
}

/** A fully-generated, randomized run. */
export interface HustleRun {
  seed: number;
  customers: Customer[];
  conditions: RoundConditions[];
}

/** Generate a randomized run from a seed (deterministic given the seed). */
export function generateRun(seed: number): HustleRun {
  const rng = mulberry32(seed);
  const customers = buildQueue(rng, seed, RUN_LENGTH);
  const conditions = buildConditions(rng, RUN_LENGTH);
  return { seed, customers, conditions };
}
